import { Controller } from "@hotwired/stimulus"
import { StreamActions, renderStreamMessage, visit } from "@hotwired/turbo"
import { Queue, SEND_DELAY, acceptsTap, paint, readCard, shown } from "../modules/card_action.mjs"
import { deliveryRequest, DELIVERY_ERROR } from "../modules/delivery_request.mjs"

// Página del carrito: la cantidad de cada línea con la cola de la vitrina (toques agrupados por línea, cantidad
// objetivo, un request a la vez), el modo de entrega, la propina y el QR. Doc: _docs/features/cart/cart_show.md
const LINE_SELECTOR = "form.cart-item-quantity"

// Los topes por línea que trae una respuesta (#cart-line-caps), o null
function capsFromStream(text) {
  const doc = new DOMParser().parseFromString(text, "text/html")
  const caps = doc.querySelector('turbo-stream[target="cart-line-caps"] template')?.content.firstElementChild?.dataset.caps
  if (!caps) return null
  try { return JSON.parse(caps) } catch { return null }
}

export default class extends Controller {
  static values = { storeId: Number, cartId: Number, nativeResume: Boolean }

  connect() {
    // Nunca restaurar el carrito desde el snapshot de Turbo: igual que no_store! con el caché HTTP, sus
    // cantidades y totales tienen que venir frescos (el "+" de la tienda ya no limpia la caché).
    // Ver _docs/frontend/catalog_presentation/storefront_quick_add.md → Decisiones (vuelta a la tienda)
    Turbo.cache.exemptPageFromCache()
    this.queue = new Queue()
    this.timers = new Map() // debounce por línea
    this.lastTap = new Map() // throttle por línea
    this.forms = new Map() // la línea de cada id tocado
    this.removing = new Set() // líneas cuyo DELETE ya salió: su cantidad no se manda ni avisa
    this.deliverySeq = 0 // número del último request del bloque de entrega
    this.deliveryRendered = 0 // el último que pintó: una respuesta más vieja que lo pintado se descarta
    this.deliveryMode = this.element.querySelector("#delivery_toggle")?.dataset.active // lo que el servidor confirmó
    this.setupQrModalListener()
  }

  // Al salir de la página, lo que esperaba el debounce sale igual, con la cantidad fijada
  disconnect() {
    this.#flush({ frozen: true })
  }

  // turbo:submit-start del tacho (ya confirmado): lo que la línea esperaba en el debounce o en cola no sale, porque
  // la línea se va; su request en vuelo, si falla, no avisa. Sin esto el PATCH salía detrás del DELETE (404)
  lineRemoving(event) {
    const id = event.currentTarget.dataset.lineId
    clearTimeout(this.timers.get(id))
    this.timers.delete(id)
    this.queue.drop(id)
    this.removing.add(id)
  }

  // turbo:submit-end del tacho sin éxito (red caída, 5xx): la línea sigue, así que se le quita la marca, vuelve a la
  // cantidad confirmada (el cambio que esperaba se descartó) y se avisa; un cambio posterior vuelve a funcionar
  lineRemovalEnded(event) {
    if (event.detail.success) return
    const form = event.currentTarget
    const id = form.dataset.lineId
    this.removing.delete(id)
    this.#paint({ [id]: { ...readCard(form), pending: 0 } })
    form.removeAttribute("aria-busy")
    window.toastr?.error("No se pudo eliminar el producto. Intenta de nuevo.")
  }

  clearTurboCache(event) {
    // Las mutaciones nativas entregan la barra confirmada; invalidar después
    // del retorno conserva la tienda también durante los borrados intermedios.
    if (["recede", "update"].includes(event.currentTarget.form?.dataset.nativeCartResume) &&
        typeof StreamActions.native_cart_resume === "function") return

    Turbo.cache.clear()
  }

  // Vuelve a pedir el bloque de delivery (teléfono recién verificado, reintento de la cotización)
  requestDelivery() {
    this.#deliver({ mode: "delivery" })
  }

  // El paso de teléfono (accounts/_phone_verification) avisa con turbo:submit-end de su
  // form de verificación; con éxito se vuelve a pedir el bloque de delivery, que ahora
  // pasa a la dirección. Un código inválido responde 422 y no dispara nada.
  phoneVerified(event) {
    if (event.detail.success) this.requestDelivery()
  }

  // "+" o "−" de una línea: pinta en el acto (de 1 no baja: ahí está el tacho) y agrupa los toques por línea
  updateQuantity(event) {
    const button = event.currentTarget
    const form = button.closest(LINE_SELECTOR)
    const id = form.dataset.lineId
    const delta = Number(button.value) || 1
    const now = performance.now()
    if (!acceptsTap(this.lastTap.get(id), now)) return
    const cards = this.#cards()
    if (delta < 0 && shown(cards[id]) <= 1) return
    const next = this.queue.tap(cards, id, delta)
    if (!next) return
    this.#paint(next)
    this.lastTap.set(id, now)
    this.forms.set(id, form)
    form.setAttribute("aria-busy", "true")
    clearTimeout(this.timers.get(id))
    this.timers.set(id, setTimeout(() => {
      this.timers.delete(id)
      this.queue.queue(id, { form })
      this.#drain()
    }, SEND_DELAY))
  }

  // Lo que espera el debounce sale ya; `frozen` fija la cantidad desde la propia línea
  #flush({ frozen = false } = {}) {
    for (const [id, timer] of this.timers) {
      clearTimeout(timer)
      const form = this.forms.get(id)
      this.queue.queue(id, { form, frozen, cards: { [id]: readCard(form) } })
    }
    this.timers.clear()
    this.#drain()
  }

  async #drain() {
    if (this.sending) return
    this.sending = true
    let op
    while ((op = this.queue.take(this.#cards()))) await this.#send(op)
    this.sending = false
    this.#settleAll() // también las que take descartó
  }

  // PATCH con la cantidad objetivo; la app pide además la barra confirmada (defer) para volver a la tienda sin GET.
  // Desde una página que ya se fue, keepalive: el navegador lo termina aunque cambie la página
  async #send(op) {
    try {
      const token = document.querySelector("meta[name='csrf-token']")?.content // sin meta con la protección apagada (test)
      const response = await fetch(op.form.dataset.quantityUrl, {
        method: "PATCH",
        keepalive: !this.element.isConnected,
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          ...(token ? { "X-CSRF-Token": token } : {}),
          ...(this.nativeResumeValue ? { "X-Native-Cart-Resume": "defer" } : {})
        },
        body: JSON.stringify({ final_quantity: op.target })
      })
      if (response.headers.get("Content-Type")?.includes("turbo-stream")) {
        const text = await response.text()
        if (response.ok) this.#confirm(op, text)
        else this.#reject(op, text)
      } else if (response.redirected) {
        visit(response.url) // sesión vencida: el login
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (error) {
      console.error("No se pudo actualizar el carrito", error)
      if (!this.element.isConnected || this.removing.has(op.id)) {
        // La página ya quedó atrás y el WebView cortó el request, o la línea se está borrando: lo que siga en cola
        // sale igual (keepalive) y no se avisa; la página nueva trae el dato. Nada que pintar
        this.queue.dropInflight()
      } else {
        this.#dropTimers() // un debounce vivo mandaría la cantidad "restaurada"
        this.#paint(this.queue.failed(this.#cards()))
        this.#lines().forEach(form => form.removeAttribute("aria-busy"))
        window.toastr?.error("No se pudo actualizar tu carrito. Intenta de nuevo.")
      }
    } finally {
      this.#settle(op.id)
    }
  }

  // 200: la línea queda en el objetivo; el stream trae el precio y el resumen (y en la app la barra del retorno)
  #confirm(op, text) {
    this.#paint(this.queue.ok(this.#cards(), null))
    this.#applyCaps(text)
    renderStreamMessage(text)
    this.#updateBadge()
    if (!this.nativeResumeValue) Turbo.cache.clear() // la app conserva la tienda hasta volver con la barra confirmada
    this.#followUp(op)
  }

  // 422: la línea vuelve a lo confirmado; el aviso llega como toast
  #reject(op, text) {
    clearTimeout(this.timers.get(op.id))
    this.timers.delete(op.id)
    this.#paint(this.queue.rejected(this.#cards()))
    this.#applyCaps(text)
    if (this.element.isConnected) renderStreamMessage(text)
  }

  // Los topes que trajo la respuesta: el "+" de las otras líneas del mismo producto sigue al inventario
  #applyCaps(text) {
    const caps = capsFromStream(text)
    if (!caps) return
    const cards = this.#cards()
    for (const id in cards) if (id in caps) cards[id] = { ...cards[id], available: caps[id] }
    this.#paint(cards)
  }

  // Lo tocado durante el vuelo pide un request más; nunca desde una página o una línea que ya no están
  #followUp(op) {
    if (!this.element.isConnected || !op.form.isConnected || this.removing.has(op.id) || this.timers.has(op.id) || this.queue.isQueued(op.id)) return
    const card = readCard(op.form)
    if (shown(card) !== card.confirmed) this.queue.queue(op.id, { form: op.form })
  }

  #dropTimers() {
    this.timers.forEach(timer => clearTimeout(timer))
    this.timers.clear()
  }

  // Ocupada mientras tenga debounce, cola o vuelo
  #busy(id) {
    return this.timers.has(id) || this.queue.isQueued(id) || this.queue.busyId === id
  }

  #settle(id) {
    if (!this.#busy(id)) this.forms.get(id)?.removeAttribute("aria-busy")
  }

  #settleAll() {
    this.forms.forEach((form, id) => this.#settle(id))
  }

  #lines() {
    return Array.from(this.element.querySelectorAll(LINE_SELECTOR))
  }

  // Las líneas por id: lo confirmado, lo pendiente y el tope de cada una (data-*)
  #cards() {
    const cards = {}
    for (const form of this.#lines()) cards[form.dataset.lineId] = readCard(form)
    return cards
  }

  // Pinta lo que cambió: los data-* y el "+" (paint), más el número del input
  #paint(cards) {
    for (const form of this.#lines()) {
      const card = cards[form.dataset.lineId]
      if (!card) continue
      paint(form, card)
      const input = form.querySelector(".quantity-input")
      if (input && input.value !== String(shown(card))) input.value = String(shown(card))
    }
  }

  // El badge del botón nativo del carrito: la suma de lo confirmado
  #updateBadge() {
    const button = document.querySelector('[data-controller="bridge--cart-button"]')
    if (!button) return
    const total = Object.values(this.#cards()).reduce((sum, card) => sum + card.confirmed, 0)
    button.setAttribute("data-bridge--cart-button-badge-value", String(total))
  }

  setupQrModalListener() {
    const qrCodeModal = document.getElementById("qrCodeModal")
    if (qrCodeModal && !qrCodeModal.dataset.listenerAttached) {
      qrCodeModal.addEventListener("hidden.bs.modal", () => {
        this.purgeQrCodeAndOrder()
      })
      qrCodeModal.dataset.listenerAttached = "true"
    }
  }

  purgeQrCodeAndOrder() {
    fetch("/purge_order", {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ order_id: window.currentOrderId })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log("Orden y QR eliminados")
        } else {
          console.log("Error al eliminar la orden o el QR.")
        }
      })
  }

  // Local/Delivery: pinta en el acto y manda el modo; si el request falla vuelve al modo confirmado y avisa
  setDeliveryMode(event) {
    const mode = event.currentTarget.dataset.mode
    const toggle = event.currentTarget.closest(".cart-delivery-toggle")
    this.#paintToggle(toggle, mode)
    this.#deliver({ mode }, { onError: () => this.#paintToggle(toggle, this.deliveryMode) })
  }

  #paintToggle(toggle, mode) {
    toggle.setAttribute("data-active", mode)
    toggle.querySelectorAll(".cart-delivery-toggle__btn").forEach(button => button.classList.toggle("active", button.dataset.mode === mode))
  }

  // POST del bloque de entrega. Las respuestas pintan en el orden en que llegan, salvo una más vieja que lo ya
  // pintado (dos toques seguidos no dejan ganar a la respuesta vieja); lo que el servidor aceptó queda como
  // modo confirmado. Una falla avisa y deja al que llamó volver a ese modo: si Delivery se guardó y Local falló,
  // la pantalla se queda en Delivery con sus bloques
  async #deliver(body, { onError } = {}) {
    const seq = ++this.deliverySeq
    try {
      const stream = await deliveryRequest(`/stores/${this.storeIdValue}/carts/${this.cartIdValue}/set_delivery_mode`, body, { visit })
      if (seq < this.deliveryRendered) return
      this.deliveryRendered = seq
      this.deliveryMode = body.mode
      if (stream) renderStreamMessage(stream)
    } catch (error) {
      if (seq < this.deliveryRendered) return
      console.error("No se pudo actualizar la entrega", error)
      onError?.()
      window.toastr?.error(DELIVERY_ERROR)
    }
  }

  setTip(event) {
    const amount = parseInt(event.currentTarget.dataset.amount)
    this.updateTip(amount)

    document.querySelectorAll(".cart-tip__pill").forEach(el => el.classList.remove("active"))
    event.currentTarget.classList.add("active")
    document.getElementById("custom_tip_container").style.display = "none"

    const pill = document.getElementById("custom_tip_pill")
    pill.textContent = "Otro"
    document.getElementById("custom_tip_input").value = ""
  }

  // Abre el input sin tocar la propina aplicada: no mueve el renglon ni desmarca la
  // pastilla vigente. Hasta que no se confirme, lo que se ve sigue siendo lo que el
  // servidor tiene y lo que se va a cobrar.
  showCustomTip() {
    document.getElementById("custom_tip_container").style.display = "block"
    document.getElementById("custom_tip_input").focus()
  }

  confirmCustomTipKey(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.confirmCustomTip()
    }
  }

  confirmCustomTip() {
    const input = document.getElementById("custom_tip_input")
    const amount = parseInt(input.value) || 0
    const pill = document.getElementById("custom_tip_pill")

    this.updateTip(amount)
    document.getElementById("custom_tip_container").style.display = "none"

    if (amount > 0) {
      pill.innerHTML = `<span>Otro</span><span>$ ${amount.toLocaleString("es-CL")}</span>`
    } else {
      pill.textContent = "Otro"
    }

    // El resaltado se mueve acá, no al abrir el input: hasta confirmar, la pastilla
    // marcada tiene que ser la propina que realmente esta aplicada.
    document.querySelectorAll(".cart-tip__pill").forEach(el => el.classList.remove("active"))
    pill.classList.add("active")
  }

  // Unico lugar que mueve el renglon de la propina, y lo hace junto con el PATCH: el numero visible no
  // puede separarse del que el servidor va a congelar. Una falla avisa (la propina está pausada)
  updateTip(amount) {
    document.getElementById("tip_display").textContent = `$${amount.toLocaleString("es-CL")}`

    deliveryRequest(`/stores/${this.storeIdValue}/carts/${this.cartIdValue}/set_delivery_tip`, { tip: amount }, { method: "PATCH", visit })
      .catch(error => {
        console.error("No se pudo guardar la propina", error)
        window.toastr?.error(DELIVERY_ERROR)
      })
  }

  // Borra la dirección y vuelve a pedirla
  changeAddress() {
    this.#deliver({ mode: "delivery", clear_address: true })
  }
}
